import {
  getInboundSourceCode as getCodeForSource,
  isInboundSourceCode,
  sourceFromReferrer,
  type InboundSourceCode,
} from "@/lib/inbound-source";
import { hasConsent } from "@/lib/legal/consent";

export const CTA_EVENT_ID_PREFIX = "cta";
export const LEAD_REF_STORAGE_KEY = "eclat_lead_ref";
export const INBOUND_SOURCE_STORAGE_KEY = "eclat_inbound_source_code";

const PLACEMENT_ALIASES: Record<string, string> = {
  header_lab_group: "header",
  practical_hub_after_logistics: "after_logistics",
  practical_hub_after_logistics_1to1: "after_logistics",
  practical_hub_after_logistics_lab_group: "after_logistics",
};

export function normalizeCtaPlacement(value?: string | null): string {
  const normalized = (value ?? "unknown")
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase()
    .replace(/1-to-1/g, "1to1")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_");

  if (!normalized) return "unknown";
  if (PLACEMENT_ALIASES[normalized]) return PLACEMENT_ALIASES[normalized];
  if (normalized.endsWith("_practical_1to1")) return "hero";
  if (normalized.endsWith("_lab_group")) return "hero";
  return normalized.replace(/_1to1$/, "");
}

export function createCtaEventId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return `${CTA_EVENT_ID_PREFIX}-${crypto.randomUUID()}`;
  }
  return `${CTA_EVENT_ID_PREFIX}-${Date.now()}-${Math.random()
    .toString(16)
    .slice(2)}`;
}

function createLeadRef(): string {
  const randomPart =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID().replace(/-/g, "").slice(0, 10)
      : `${Date.now().toString(36)}${Math.random()
          .toString(36)
          .slice(2)}`.slice(0, 10);
  return `ECL-${randomPart.toUpperCase()}`;
}

export function getOrCreateLeadRef(): string {
  if (typeof window === "undefined") return createLeadRef();

  try {
    const existing = window.sessionStorage.getItem(LEAD_REF_STORAGE_KEY);
    if (existing) return existing;
    const generated = createLeadRef();
    window.sessionStorage.setItem(LEAD_REF_STORAGE_KEY, generated);
    return generated;
  } catch {
    return createLeadRef();
  }
}

function sourceCodeFromUrl(href: string): InboundSourceCode | null {
  try {
    return getCodeForSource(new URL(href).searchParams.get("utm_source"));
  } catch {
    return null;
  }
}

export function captureInboundSourceCode(
  href = typeof window !== "undefined" ? window.location.href : "",
  referrer = typeof document !== "undefined" ? document.referrer : ""
): InboundSourceCode | null {
  if (typeof window === "undefined") return null;

  try {
    const fromUtm = sourceCodeFromUrl(href);
    if (fromUtm) {
      window.sessionStorage.setItem(INBOUND_SOURCE_STORAGE_KEY, fromUtm);
      return fromUtm;
    }

    const existing = window.sessionStorage.getItem(INBOUND_SOURCE_STORAGE_KEY);
    if (isInboundSourceCode(existing)) return existing;

    const fromReferrer = getCodeForSource(sourceFromReferrer(referrer));
    if (fromReferrer) {
      window.sessionStorage.setItem(INBOUND_SOURCE_STORAGE_KEY, fromReferrer);
      return fromReferrer;
    }
  } catch {
    return (
      sourceCodeFromUrl(href) ?? getCodeForSource(sourceFromReferrer(referrer))
    );
  }

  return null;
}

export function clearInboundSourceCode(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(INBOUND_SOURCE_STORAGE_KEY);
  } catch {
    // Storage can be unavailable in privacy-restricted browsers.
  }
}

export function getSessionInboundSourceCode(): InboundSourceCode | null {
  if (!hasConsent("analytics")) {
    clearInboundSourceCode();
    return null;
  }
  return captureInboundSourceCode();
}

export function appendLeadAttribution(
  message: string,
  leadRef: string,
  sourceCode?: InboundSourceCode | null
): string {
  let result = message.trim();
  if (!result.includes(`Ref: ${leadRef}`)) {
    result = `${result}\n\nRef: ${leadRef}`;
  }
  if (sourceCode && !result.includes(`Source: ${sourceCode}`)) {
    result = `${result}\nSource: ${sourceCode}`;
  }
  return result;
}

export function appendLeadRef(message: string, leadRef: string): string {
  return appendLeadAttribution(message, leadRef);
}

export function normalizeLeadRef(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toUpperCase();
  return /^ECL-[A-Z0-9]{8,24}$/.test(normalized) ? normalized : null;
}
